import React, { FC, PropsWithChildren, useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
// material-ui
import {
  Box,
  Grid,
  Typography,
  SelectChangeEvent,
  Button,
  Avatar,
  Select,
  Checkbox,
  MenuItem,
  ListItemText,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  TextField,
  Chip,
  Divider,
  Dialog,
} from "@mui/material";
import CustomTextField from "../FormFields/CustomTextField";
import {
  IAccountBasicFormInputs
} from "../../utils/account.types";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { StyledInput } from './Dialog.styles';
import CustomSelect, { IOptions } from "../FormFields/CustomSelect";
import LoadingButton from "@mui/lab/LoadingButton";
import { ApiStatus } from "../../utils/api.types";
import { useAppSelector } from "../../store/hooks";

export interface IDialogProps {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  data?: any;
}

// Schema to validate account form.
const schema = yup.object().shape({
  name: yup.string().required("Name is required."),
  legalName: yup.string().required("Legal Name is required."),
  taxIdentificationNumber1: yup.string().required("Tax Identification #1 (PAN for India) is required."),
  taxIdentificationNumber2: yup.string().required("Tax Identification #2 (GST for India) is required."),
});

//Custom Dialog Component.
const CustomDialogForm: FC<PropsWithChildren<IDialogProps>> = ({
  title,
  onClose,
  open,
  children,
  onSubmit,
  data,
}) => {
  const [industryVerticalOptions, setIndustryVerticalOptions] = useState<IOptions[]>([]);
  const [selectedIndustryVertical, setSelectedIndustryVertical] = useState('');
  const { status } = useAppSelector((state) => state.account);
  const [image, setImage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [signImage, setSignImage] = useState("");
  const [selectedSignFile, setSelectedSignFile] = useState<File>();

  const [selectedBusinessType, setSelectedBusinessType] = React.useState<string[]>([]);

  const [checked, setChecked] = useState(true);
  const [logoChecked, setLogoChecked] = useState(true);

  const businessTypeOptions = [
    'Product',
    'Service',
    'Consulting'
  ];

  // Populate Industry Vertical on Account form load
  useEffect(() => {
    let options = [
      {
        label: "Aerospace (aircraft manufacturing)",
        value: "Aerospace (aircraft manufacturing)",
      },
      { label: "Agriculture", value: "Agriculture" },
      { label: "Chemical (manufacturing)", value: "Chemical (manufacturing)" },
      { label: "Computers", value: "Computers" },
      { label: "Construction", value: "Construction" },
      { label: "Defense", value: "Defense" },
      {
        label: "Energy (production, distribution)",
        value: "Energy (production, distribution)",
      },
      { label: "Entertainment", value: "Entertainment" },
      { label: "Financial", value: "Financial" },
      { label: "Food", value: "Food" },
      { label: "Health Care", value: "Health Care" },
      { label: "Hospitality", value: "Hospitality" },
      { label: "Information", value: "Information" },
      { label: "Manufacturing", value: "Manufacturing" },
      { label: "Mass Media", value: "Mass Media" },
      { label: "Telecommunications", value: "Telecommunications" },
      { label: "Transport", value: "Transport" },
      { label: "Water", value: "Water" },
    ];
    setIndustryVerticalOptions(options);
  }, []);

  // use form
  const methods = useForm<IAccountBasicFormInputs>({
    resolver: yupResolver(schema)
  });

  // Method to set preview of selected picture.
  const handleSelectFile = (event: any) => {
    const target = event.target as HTMLInputElement;
    console.log(target.files);

    if (target.files && target.files[0]) {
      setSelectedFile(target.files[0]);
      setImage(URL.createObjectURL(target.files[0]));
    }
  };

  const handleSignSelectFile = (event: any) => {
    const target = event.target as HTMLInputElement;
    console.log(target.files);

    if (target.files && target.files[0]) {
      setSelectedSignFile(target.files[0]);
      setSignImage(URL.createObjectURL(target.files[0]));
    }
  };

  const innerSubmit = async (data: any) => {
    const form = new FormData();
    form.append('name', data.name);
    form.append('legalName', data.legalName);
    form.append('industryVertical', data.industryVertical);
    form.append('displayName', data.displayName);
    form.append('companyDesc', data.companyDesc);
    form.append('businessType', JSON.stringify(selectedBusinessType));
    form.append('taxIdentificationNumber1', data.taxIdentificationNumber1);
    form.append('taxIdentificationNumber2', data.taxIdentificationNumber2);
    form.append('website', data.website);
    form.append('contactName', data.contactName);
    form.append('email', data.email);
    form.append('whatsAppNumber', data.whatsAppNumber);

    if (checked == true) {
      form.append('showSignatureOnInvoice', 'true');
    } else {
      form.append('showSignatureOnInvoice', 'false');
    }

    if (logoChecked == true) {
      form.append('showLogoOnInvoice', 'true');
    } else {
      form.append('showLogoOnInvoice', 'false');
    }

    if (selectedFile) {
      form.append('logo', selectedFile);
    }
    if (selectedSignFile) {
      form.append('signature', selectedSignFile);
    }
    onSubmit(form);
  };

  const handleSelectChange = (
    event: SelectChangeEvent<typeof selectedBusinessType>
  ) => {
    const {
      target: { value },
    } = event;
    setSelectedBusinessType(
      typeof value === "string" ? value.split(",") : value
    );
  };

  useEffect(() => {
    let basicInfoAvaliable = true;

    const setInitialData = () => {
      try {
        if (data && basicInfoAvaliable) {
          methods.setValue('name', data.name);
          methods.setValue('displayName', data.displayName);
          methods.setValue('legalName', data.legalName);
          methods.setValue('companyDesc', data.companyDesc);
          methods.setValue('website', data.website);
          methods.setValue('taxIdentificationNumber1', data.taxIdentificationNumber1);
          methods.setValue('taxIdentificationNumber2', data.taxIdentificationNumber2);
          methods.setValue('contactName', data.contactName);
          methods.setValue('email', data.email);
          methods.setValue('whatsAppNumber', data.whatsAppNumber);
          if (data.businessType) setSelectedBusinessType(data.businessType);
          setSelectedIndustryVertical(data.industryVertical);
          setImage(data.logo);
          setSignImage(data.signature);
          setLogoChecked(data.showLogoOnInvoice);
          setChecked(data.showSignatureOnInvoice);
        }
      } catch (error: any) {
        console.log("Dialog catch block called");
      }
    };

    setInitialData();

    return () => {
      basicInfoAvaliable = false;
    };
  }, [data, methods]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '1000px',
          height: '98vh',
          maxHeight: '998px',
          margin: '20px 0',
        }
      }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(innerSubmit)}
          noValidate
          sx={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <Grid container >
            <Grid {...{ xs: 12, sm: 8, md: 4, lg: 12, p: 2, pl: 2, bgcolor: '#e9ecf1' }} style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }} >
              <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
                <LoadingButton
                  type="submit"
                  name={'submit'}
                  // disabled={!methods.formState.isValid}
                  loading={status === ApiStatus.LOADING}
                  style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                  variant="contained"
                  size='small'>
                  {`Save`}
                </LoadingButton>

                <Button
                  style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                  variant="contained"
                  size='small'
                  onClick={() => {
                    methods.reset();
                    onClose();
                  }}>
                  Cancel
                </Button>
              </div>
              <h3 style={{ marginTop: -2 }}>{title}</h3>
            </Grid>

          </Grid>

          <DialogContent sx={{ flex: 1, overflow: "auto", p: 3 }}>
            <Grid container spacing={2} justifyContent="left">
              {/* Logo and Signature Section */}
              {/* <Grid item xs={12} mt={1}>
                <Divider style={{ marginRight: -8 }}>
                  <Chip label="Logo & Signature" size="small" />
                </Divider>
              </Grid> */}

              <Grid item xs={12} md={12} sm={12}>
                <div style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "row",
                  gap: "20px",
                  alignItems: "center",
                  justifyContent: "center",  // This centers the entire container
                  width: "100%"
                }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div className="file-name">
                      {image && (
                        <Avatar
                          alt={"Picture"}
                          imgProps={{ referrerPolicy: "no-referrer" }}
                          src={image}
                          style={{
                            width: "120px",
                            height: "80px",
                            objectFit: "contain",
                          }}
                          variant='square'
                          sizes={"lg"}
                        />
                      )}
                    </div>
                    <label htmlFor="logo">
                      <StyledInput
                        accept="image/*"
                        id="logo"
                        type="file"
                        onChange={handleSelectFile}
                      />
                      <Button
                        variant="outlined"
                        className="btn-choose"
                        component="span"
                        size="small"
                      >
                        Choose Logo
                      </Button>
                    </label>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={logoChecked}
                          onChange={(event) => {
                            setLogoChecked(event.target.checked);
                          }}
                          name="logoChecked"
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography variant="caption">Show Logo On Invoice</Typography>}
                    />
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div className="file-name">
                      {signImage && (
                        <Avatar
                          alt={"Picture"}
                          imgProps={{ referrerPolicy: "no-referrer" }}
                          src={signImage}
                          style={{
                            width: "120px",
                            height: "80px",
                            objectFit: "contain",
                          }}
                          variant='square'
                          sizes={"lg"}
                        />
                      )}
                    </div>
                    <label htmlFor="signature">
                      <StyledInput
                        accept="image/*"
                        id="signature"
                        type="file"
                        onChange={handleSignSelectFile}
                      />
                      <Button
                        variant="outlined"
                        className="btn-choose"
                        component="span"
                        size="small"
                      >
                        Choose Signature
                      </Button>
                    </label>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={(event) => {
                            setChecked(event.target.checked);
                          }}
                          name="signatureChecked"
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography variant="caption">Show Signature On Invoice</Typography>}
                    />
                  </div>
                </div>
              </Grid>

              {/* Main Content Row with Business Information and Tax Information */}
              <Grid item xs={12} mt={1}>
                <Grid container spacing={2}>
                  {/* Business Information Section - Left Side */}
                  <Grid item xs={12} md={8}>
                    <Divider style={{ marginRight: -8 }}>
                      <Chip label="Business Information" size="small" />
                    </Divider>

                    <Grid container spacing={2} mt={1}>
                      {/* Column 1 */}
                      <Grid item xs={12} md={6}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <h5 className='c-label'>Brand Name</h5>
                            <TextField
                              placeholder='Brand Name'
                              {...methods.register('name')}
                              className='c-text-field-4'
                              variant="standard"
                              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                              InputProps={{
                                disableUnderline: true,
                              }}
                              required
                              size='small'
                            />
                            <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.name?.message ?? ""}</p>
                          </Grid>

                          <Grid item xs={12}>
                            <h5 className='c-label'>Display Name</h5>
                            <TextField
                              placeholder='Display Name'
                              {...methods.register('displayName')}
                              size='small'
                              className='c-text-field-4'
                              variant="standard"
                              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                              InputProps={{
                                disableUnderline: true,
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <h5 className='c-label'>Legal Name</h5>
                            <TextField
                              placeholder='Legal Name'
                              {...methods.register('legalName')}
                              required
                              size='small'
                              className='c-text-field-4'
                              variant="standard"
                              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                              InputProps={{
                                disableUnderline: true,
                              }}
                            />
                            <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.legalName?.message ?? ""}</p>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Column 2 */}
                      <Grid item xs={12} md={6}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <h5 className='c-label'>Company Description</h5>
                            <TextField
                              placeholder='Company Description'
                              {...methods.register('companyDesc')}
                              size='small'
                              multiline
                              rows={2}
                              className='c-text-field-4'
                              variant="standard"
                              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                              InputProps={{
                                disableUnderline: true,
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <h5 className='c-label' style={{ marginTop: '4px' }}>Website</h5>
                            <TextField
                              placeholder='Website'
                              {...methods.register('website')}
                              size='small'
                              className='c-text-field-4'
                              variant="standard"
                              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                              InputProps={{
                                disableUnderline: true,
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <h5 className='c-label'>Business Type</h5>
                            <Select
                              multiple
                              value={selectedBusinessType}
                              onChange={handleSelectChange}
                              renderValue={(selected: any) => selected.join(", ")}
                              size='small'
                              disableUnderline
                              variant='outlined'
                              sx={{
                                width: '100%',
                                mt: 0.5,
                                backgroundColor: '#fff',
                                borderRadius: '10px',
                                padding: '8px 12px',
                                '& .MuiSelect-select': {
                                  padding: '0',
                                  display: 'flex',
                                  alignItems: 'center',
                                },
                                '& .MuiInput-underline:before': {
                                  display: 'none',
                                },
                                '& .MuiInput-underline:after': {
                                  display: 'none',
                                },
                                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                  display: 'none',
                                },
                              }}
                            >
                              {businessTypeOptions.map((name) => (
                                <MenuItem key={name} value={name}>
                                  <Checkbox
                                    checked={selectedBusinessType.indexOf(name) > -1}
                                    size="small"
                                  />
                                  <ListItemText primary={name} />
                                </MenuItem>
                              ))}
                            </Select>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Tax Information Section - Right Side */}
                  <Grid item xs={12} md={4}>
                    <Divider style={{ marginRight: -8 }}>
                      <Chip label="Tax Information" size="small" />
                    </Divider>

                    <Grid container spacing={2} mt={1}>
                      <Grid item xs={12}>
                        <h5 className='c-label'>Tax Id #1 (PAN for India)</h5>
                        <TextField
                          placeholder='PAN Number'
                          {...methods.register('taxIdentificationNumber1')}
                          required
                          size='small'
                          className='c-text-field-4'
                          variant="standard"
                          sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                          InputProps={{
                            disableUnderline: true,
                          }}
                        />
                        <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.taxIdentificationNumber1?.message ?? ""}</p>
                      </Grid>

                      <Grid item xs={12}>
                        <h5 className='c-label'>Tax Id #2 (GST for India)</h5>
                        <TextField
                          placeholder='GST Number'
                          {...methods.register('taxIdentificationNumber2')}
                          required
                          size='small'
                          className='c-text-field-4'
                          variant="standard"
                          sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                          InputProps={{
                            disableUnderline: true,
                          }}
                        />
                        <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.taxIdentificationNumber2?.message ?? ""}</p>
                      </Grid>

                      <Grid item xs={12}>
                        <h5 className='c-label'>Industry Vertical</h5>
                        {industryVerticalOptions?.length &&
                          <Select
                            {...methods.register('industryVertical')}
                            value={selectedIndustryVertical ? selectedIndustryVertical : ''}
                            onChange={(event: SelectChangeEvent<any>) => {
                              setSelectedIndustryVertical(event.target.value);
                            }}
                            size='small'
                            sx={{
                              width: '100%',
                              mt: 0.5,
                              backgroundColor: '#fff',
                              borderRadius: '10px',
                              padding: '8px 12px',
                              '& .MuiSelect-select': {
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                              },
                              '& .MuiInput-underline:before': {
                                display: 'none',
                              },
                              '& .MuiInput-underline:after': {
                                display: 'none',
                              },
                              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                display: 'none',
                              },
                            }}
                          >
                            {industryVerticalOptions && industryVerticalOptions.map((option) =>
                              <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                            )}
                          </Select>
                        }
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>



              {/* Contact Information Section */}
              <Grid item xs={12} mt={1}>
                <Divider style={{ marginRight: -8 }}>
                  <Chip label="Contact Information" size="small" />
                </Divider>
              </Grid>

              <Grid item xs={4}>
                <h5 className='c-label'>Contact Name</h5>
                <TextField
                  placeholder='Contact Name'
                  {...methods.register('contactName')}
                  size='small'
                  className='c-text-field-4'
                  variant="standard"
                  sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </Grid>

              <Grid item xs={4}>
                <h5 className='c-label'>Email</h5>
                <TextField
                  placeholder='Email'
                  {...methods.register('email')}
                  size='small'
                  className='c-text-field-4'
                  variant="standard"
                  sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </Grid>

              <Grid item xs={4}>
                <h5 className='c-label'>WhatsApp Number</h5>
                <TextField
                  placeholder='WhatsApp Number'
                  {...methods.register('whatsAppNumber')}
                  size='small'
                  className='c-text-field-4'
                  variant="standard"
                  sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </Grid>
            </Grid>
            {children}
          </DialogContent>
        </Box>
      </FormProvider>
    </Dialog>
  );
};

export default CustomDialogForm;